import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Tooltip, OverlayTrigger } from "react-bootstrap";

interface DirectionProps {
  isRTL: boolean;
}

interface AttendanceData {
  inTimeFormatted: string;
  outTimeFormatted: string;
  status: string;
  dateFormattedEn: string;
  dateFormattedAr: string;
}

export const UserAttendanceComponent = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const { i18n, t } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [attendance, setAttendance] = useState<AttendanceData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Acquire token silently (Memoized)
  const fetchToken = useCallback(async () => {
    if (!accounts.length) return;

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["User.Read"], // Adjust as per API requirements
        account: accounts[0],
      });

      if (response.accessToken) {
        setToken(response.accessToken);
      } else {
        console.error("No access token found in response");
      }
    } catch (err) {
      console.error("Token acquisition failed", err);
      // setError("Authentication failed. Please log in again.");
    }
  }, [instance, accounts]);

  // Fetch attendance data (Memoized)
  const fetchAttendance = useCallback(async (accessToken: string) => {
    try {
      const response = await axios.get(
        "https://thc-attendance-prd-dqgaetdshfcafdg4.westeurope-01.azurewebsites.net/TimeAttendance/GetMyAttendanceSummary",
        {
          headers: {
             Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.status === "1" && response.data.result) {
        setAttendance(response.data.result);
      } else {
        setAttendance(null);
      }
    } catch (err) {
      console.error("Error fetching attendance data:", err);
      // setError("Error fetching attendance data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accounts.length) {
      fetchToken();
    }
  }, [accounts, fetchToken]);

  useEffect(() => {
    if (token) {
      fetchAttendance(token);

      const interval = setInterval(() => {
        fetchAttendance(token);
      }, 5 * 60 * 1000); // every 5 minutes

      return () => clearInterval(interval);
    }
  }, [token, i18n.language, fetchAttendance]);

  if (!accounts.length) return <></>;
  if (!attendance) return null;

  const formattedDate = isRTL
    ? attendance.dateFormattedAr
    : attendance.dateFormattedEn;

    const handleClick = () => navigate("/attendance");


  return (
    <OverlayTrigger
      placement={isRTL ? "bottom" : "bottom"}
      overlay={<Tooltip id="attendance-tooltip">{t("attendance_log")}</Tooltip>}
      delay={{ show: 250, hide: 200 }}

    >
    <div style={{ display: "inline-block" }} onClick={handleClick}>
    <TimeBlockContainer
        dir="ltr"
        isRTL={isRTL}
      >
        {loading ? (
          <LoadingText>{t("Loading...")}</LoadingText>
        ) : error ? (
          <ErrorText>{t(error)}</ErrorText>
        ) : (
          <>
            <TimeRow isRTL={isRTL}>
              <DateDisplay>{formattedDate}</DateDisplay>

              <TimeBlock>
                <LabelContainer>
                  <svg
                    width="20px"
                    height="20px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        d="M2.00098 11.999L16.001 11.999M16.001 11.999L12.501
8.99902M16.001 11.999L12.501 14.999"
                        stroke="#27995F"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                      <path
                        opacity="0.5"
                        d="M9.00195 7C9.01406 4.82497 9.11051
3.64706 9.87889 2.87868C10.7576 2 12.1718 2 15.0002 2L16.0002 2C18.8286 2 20.2429
2 21.1215 2.87868C22.0002 3.75736 22.0002 5.17157 22.0002 8L22.0002 16C22.0002
18.8284 22.0002 20.2426 21.1215 21.1213C20.2429 22 18.8286 22 16.0002
22H15.0002C12.1718 22 10.7576 22 9.87889 21.1213C9.11051 20.3529 9.01406 19.175
9.00195 17"
                        stroke="#27995F"
                        stroke-width="1.5"
                        stroke-linecap="round"
                      ></path>
                    </g>
                  </svg>
                  <LabelText>{t("In")}</LabelText>
                </LabelContainer>
                <TimeText>{attendance?.inTimeFormatted || "--:--"}</TimeText>
              </TimeBlock>

              <OutTimeBlock>
                <LabelContainer>
                  <svg
                    width="20px"
                    height="20px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    stroke="#ff0000"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        opacity="0.5"
                        d="M9.00195 7C9.01406 4.82497 9.11051
3.64706 9.87889 2.87868C10.7576 2 12.1718 2 15.0002 2L16.0002 2C18.8286 2 20.2429
2 21.1215 2.87868C22.0002 3.75736 22.0002 5.17157 22.0002 8L22.0002 16C22.0002
18.8284 22.0002 20.2426 21.1215 21.1213C20.2429 22 18.8286 22 16.0002
22H15.0002C12.1718 22 10.7576 22 9.87889 21.1213C9.11051 20.3529 9.01406 19.175
9.00195 17"
                        stroke="#ff0000"
                        stroke-width="1.5"
                        stroke-linecap="round"
                      ></path>
                      <path
                        d="M15 12L2 12M2 12L5.5 9M2 12L5.5 15"
                        stroke="#ff0000"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </g>
                  </svg>
                  <LabelText>{t("Out")}</LabelText>
                </LabelContainer>
                <TimeText>{attendance?.outTimeFormatted || "--:--"}</TimeText>
              </OutTimeBlock>
            </TimeRow>
          </>
        )}
      </TimeBlockContainer>
      </div>
    </OverlayTrigger>
  );
};

// Styled Components
const TimeBlockContainer = styled.div<DirectionProps>`
  border-radius: 10px;
  border: 1px solid #efefef;
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 8px;
  cursor: pointer;
  width: ${({ isRTL }) => (isRTL ? "280px" : "263px")};

  ${({ isRTL }) =>
    isRTL &&
    css`
      flex-direction: row-reverse;
    `}
`;

const TimeBlock = styled.div`
  top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  border: 1px solid #efefef;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const OutTimeBlock = styled(TimeBlock)`
  background: #efefef;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const LabelText = styled.span`
  font-size: 0.75rem;
`;

const TimeText = styled.span`
  font-size: 1rem;
  font-weight: bold;
`;

const LoadingText = styled.span`
  font-size: 0.9rem;
  color: #888;
  text-align: center;
`;

const ErrorText = styled.span`
  font-size: 0.9rem;
  color: red;
  text-align: center;
`;
const DateDisplay = styled.div`
  font-size: 0.75rem;
  color: #444;
  margin-bottom: 6px;
  text-align: ${({ dir }) => (dir === "rtl" ? "right" : "left")};
  width: 100%;
  display: flex;
  align-items: center;
  gap: 5px;
`;
const TimeRow = styled.div<DirectionProps>`
  display: flex;
  flex-direction: ${({ isRTL }) => (isRTL ? "row-reverse" : "row")};
  gap: 10px;
`;

export default UserAttendanceComponent;
